import React, { useMemo } from 'react';

import { RecordContextProvider, useRecordContext } from 'react-admin';

import { Box, Typography } from '@mui/material';

import { FormAnswerConflictStatus } from '../../../constants/formAnswerConflictStatus';
import { ConflictFields } from '../../../constants/conflictFields';
import conflictFieldsFormatter from '../../conflict/conflictFieldsFormatter';
import ConflictStatusIcon from '../../conflict/ConflictStatusIcon';

const DEFAULT_SX = {
  wordBreak: 'break-all',
  wordWrap: 'normal',
};

const OTHER_SELECTED_VALUE_SX = {
  ...DEFAULT_SX,
  textDecoration: 'line-through',
};

export const DisplayValues = ({
  label,
  children,
  shouldDisplayOtherSelectedValue,
  selectedValue,
  datapoint,
}: {
  label:string;
  children?: React.ReactElement | false;
  selectedValue?: unknown;
  shouldDisplayOtherSelectedValue: boolean;
  datapoint: ConflictFields | null;
}) => {
  const record = useRecordContext();

  const transformedValue = useMemo(() => {
    if (!selectedValue || !datapoint) return selectedValue;

    const formatter = conflictFieldsFormatter[datapoint];
    return formatter ? formatter(selectedValue) : selectedValue;
  }, [datapoint, selectedValue]);

  return (
    <>
      <Typography variant="label">{label}</Typography>
      <RecordContextProvider
        value={{
          ...record,
          ...(transformedValue ? { value: transformedValue } : {}),
          sx: shouldDisplayOtherSelectedValue ? OTHER_SELECTED_VALUE_SX : DEFAULT_SX,
        }}
      >
        {children}
      </RecordContextProvider>
    </>
  );
};

const AnswerTitle = ({
  status,
  label,
  children,
  selectedValue,
  shouldDisplayOtherSelectedValue,
  datapoint,
}: {
  status: FormAnswerConflictStatus;
  label:string;
  children?: React.ReactElement | false;
  selectedValue?: unknown;
  shouldDisplayOtherSelectedValue: boolean;
  datapoint: ConflictFields | null;
}) => (
  <Box display="flex" flexDirection="column" gap={2}>
    <Box display="flex" width="100%" gap={4} alignItems="center">
      <ConflictStatusIcon status={status} />
      <Box display="flex" flexDirection="column">
        <DisplayValues
          label={label}
          shouldDisplayOtherSelectedValue={shouldDisplayOtherSelectedValue}
          datapoint={datapoint}
        >
          {children}
        </DisplayValues>
      </Box>
    </Box>
    {shouldDisplayOtherSelectedValue && (
      <Box display="flex" flexDirection="column" pl={10}>
        <DisplayValues
          label="Currently selected value"
          shouldDisplayOtherSelectedValue={false}
          selectedValue={selectedValue}
          datapoint={datapoint}
        >
          <Box display="flex" flexWrap="wrap" minWidth={0}>
            {children}
          </Box>
        </DisplayValues>
      </Box>
    )}
  </Box>
);

export default AnswerTitle;
